import countries from 'i18n-iso-countries';
import React, { FC, Fragment, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Asset, ITaxArrayRatesValues, ITaxTypesAndRates, SPV } from 'src/models';
import { EApplicableTo, ETaxType } from 'src/models/enum';
import { TSecurityType } from '@brickwise/asset-validation/dist/schema/enums';
import { calcEmittedBricks } from 'src/util/utils';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { EarningPeriod } from './forms/EarningPeriod';

export const checkIfCapitalGainsTaxesApply = (assetTSecurityType: TSecurityType | undefined): boolean => {
  switch (assetTSecurityType) {
    case TSecurityType.participationRightDebtCapital:
    case TSecurityType.participationRightDebtCapitalPartialSale:
      return true;
    default:
      return false;
  }
};

export const checkCapitalGainsTaxType = (spv: SPV): ITaxTypesAndRates[] => {
  const country = spv.address.countryCode;
  switch (country) {
    case countries.getAlpha3Code('Austria', 'en'):
    case 'Österreich' as countries.Alpha3Code: // Legacy Support
    case 'AUT' as countries.Alpha3Code: // Legacy Support
      return [{ taxType: ETaxType.KESt, taxRate: 27.5, applicableTo: EApplicableTo.capitalGains }];
    case countries.getAlpha3Code('Germany', 'en'):
    case 'Deutschland' as countries.Alpha3Code: // Legacy Support
    case 'DEU' as countries.Alpha3Code: // Legacy Support
      return [
        { taxType: ETaxType.KapESt, taxRate: 24.4499, applicableTo: EApplicableTo.capitalGains },
        { taxType: ETaxType.Soli, taxRate: 5.5, applicableTo: EApplicableTo.capitalTaxes },
        { taxType: ETaxType.Kirchensteuer, taxRate: 9, applicableTo: EApplicableTo.capitalTaxes },
      ];
    default:
      return [];
  }
};

/*
 * Apply capital gains taxes to the payout
 * @param {ITaxTypesAndRates[]} taxArray - Array of tax types and rates
 * @param {number} investorProduct - The total gross before taxes amount the investor gets
 * @returns {string} - Total tax amount
 * @returns {ITaxTypesAndRates[]} - Array of tax types and rates
 */
export const applyCapitalGainsTax = (taxArray: ITaxTypesAndRates[], investorProduct: number): [string, ITaxArrayRatesValues[]] => {
  // filter for every tax type that is applicable to capital gains
  const capitalGainsTaxes = taxArray.filter((tax) => tax.applicableTo === EApplicableTo.capitalGains);
  // filter for every tax type that is applicable to capital taxes
  const superTaxes = taxArray.filter((tax) => tax.applicableTo === EApplicableTo.capitalTaxes);
  let sumOfCapitalGainsTaxes = '0.00';
  let sumOfSuperTaxes = '0.00';

  const returnArray: ITaxArrayRatesValues[] = capitalGainsTaxes.map((tax) => {
    // The gross payout to the investor is multiplied by the tax rate (ie. 100€ payout * 25% KapEst = 25€ KapEst).
    // Here we need to round instead of using toFixed because we want to round taxes. Maybe use banker's rounding soon? (see test: "Should split the rent to the investors WITH Capital Gain tax and Rounding (AUSTRIA)")
    const taxAmount = Math.round(investorProduct * tax.taxRate) / 100; // Capital Gains Taxes are to be rounded (as opposed so Soli and Kirchensteuer)
    // Add the tax amount to the total tax amount
    sumOfCapitalGainsTaxes = (Number(sumOfCapitalGainsTaxes) + taxAmount).toFixed(2);
    return { type: tax.taxType, percentage: tax.taxRate.toString(), amount: taxAmount.toFixed(2), applicableTo: tax.applicableTo };
  });

  superTaxes.forEach((tax) => {
    // The Capital Gain Taxes are multiplied by the super tax (ie. 25€ KapEst * 5.5% Soli = 1.375€ Soli).
    // Soli and Kirchensteuer are not to be rounded but cut of after the last cent, ie 0.129 = 0.12 or 1.375 Soli => 1.37€ Soli
    const taxAmount = (Math.floor(Number(sumOfCapitalGainsTaxes) * tax.taxRate) / 100).toFixed(2);
    sumOfSuperTaxes = (Number(sumOfSuperTaxes) + Number(taxAmount)).toFixed(2);
    returnArray.push({
      type: tax.taxType,
      percentage: tax.taxRate.toFixed(2),
      amount: taxAmount,
      applicableTo: tax.applicableTo,
    });
  });

  const sumOfAllTaxes = (Number(sumOfCapitalGainsTaxes) + Number(sumOfSuperTaxes)).toFixed(2);
  return [sumOfAllTaxes, returnArray];
};

export const calculateIssuerTaxes = (tax: ITaxArrayRatesValues, totalRent: number, string: boolean) => {
  if (string) return ((Number(tax.percentage) * totalRent) / 100).toFixed(2);
  else return Number(((Number(tax.percentage) * totalRent) / 100).toFixed(2));
};

const calculateInvestorTaxes = (tax: ITaxArrayRatesValues, asset: Asset, string: boolean) => {
  if (asset?.investmentDetails?.totalBricks) {
    switch (tax.applicableTo) {
      case EApplicableTo.capitalGains:
        if (string) return (Number(tax.amount) * calcEmittedBricks(asset)).toFixed(2);
        else return Number((Number(tax.amount) * calcEmittedBricks(asset)).toFixed(2));
      case EApplicableTo.capitalTaxes:
        if (string) return (Number(tax.amount) * calcEmittedBricks(asset)).toFixed(2);
        else return Number((Number(tax.amount) * calcEmittedBricks(asset)).toFixed(2));
    }
  } else {
    if (string) return '0.00';
    return 0;
  }
};

interface Props {
  asset: Asset;
  spv: SPV;
  totalRent: number;
  rentPerShareFloored: number;
  formikEarningPeriod: any;
  setIssuerTaxTotal: (value: number) => void;
}

export const TaxSystem: FC<Props> = ({ asset, spv, totalRent, rentPerShareFloored, formikEarningPeriod, setIssuerTaxTotal }) => {
  const { t } = useTranslation();
  const taxesApply = checkIfCapitalGainsTaxesApply(asset.investmentDetails.securityType);
  const taxArray = checkCapitalGainsTaxType(spv);
  const [, outputArray] = applyCapitalGainsTax(taxArray, rentPerShareFloored);
  useEffect(() => {
    if (!taxesApply) {
      setIssuerTaxTotal(0);
      return;
    }
    const issuerTaxes = outputArray.reduce((acc, tax) => acc + (calculateIssuerTaxes(tax, totalRent, false) as number), 0);
    setIssuerTaxTotal(issuerTaxes);
  }, [totalRent]);
  return (
    <>
      {taxesApply && (
        <>
          {outputArray.map((tax) => {
            if (tax.type === ETaxType.KESt && spv.address.countryCode === countries.getAlpha3Code('Austria', 'en'))
              return (
                <Fragment key={tax.type}>
                  <Grid container key={tax.type + 'issuer'}>
                    <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
                      <Typography variant="body1" color="gray">
                        {t('issuer.rent.tax.total.issuer', { taxType: tax.type, taxRate: tax.percentage })}
                      </Typography>
                    </Grid>
                    <Grid data-testid="output-issuer-taxes">€ {calculateIssuerTaxes(tax, totalRent, true)}</Grid>
                  </Grid>
                  <Grid container key={tax.type + 'investor'}>
                    <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
                      <Typography variant="body1" color="gray">
                        {t('issuer.rent.tax.total.investor', { taxType: tax.type, taxRate: tax.percentage })}
                      </Typography>
                    </Grid>
                    <Grid data-testid="output-investor-taxes">€ {calculateInvestorTaxes(tax, asset, true)}</Grid>
                  </Grid>
                  <Grid container key={tax.type + 'rounding'}>
                    <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
                      <Typography variant="body1" color="gray">
                        {t('issuer.rent.tax.roundingError.KESt')}
                      </Typography>
                    </Grid>
                    <Grid data-testid="output-rounding-error">
                      €{' '}
                      {(
                        (calculateIssuerTaxes(tax, totalRent, false) as number) - (calculateInvestorTaxes(tax, asset, false) as number)
                      ).toFixed(2)}
                    </Grid>
                  </Grid>
                </Fragment>
              );
            else if (tax.type === ETaxType.KapESt || tax.type === ETaxType.Soli || tax.type === ETaxType.Kirchensteuer)
              return (
                <React.Fragment key={tax.type}>
                  <Grid container>
                    <Grid xs={7} sm={5} sx={{ mr: 1 }} display="flex" alignItems="center">
                      <div key={tax.type}>{t('issuer.rent.tax.total.generic', { taxType: tax.type, taxRate: tax.percentage })}</div>
                    </Grid>
                    <Grid data-testid={'output-investor-taxes-' + tax.type}>€ {calculateInvestorTaxes(tax, asset, true)}</Grid>
                  </Grid>
                </React.Fragment>
              );
            else
              return (
                <React.Fragment key={tax.type}>
                  <div>
                    {t('issuer.rent.tax.total.generic', { taxType: tax.type, taxRate: tax.percentage })} - {spv.address.countryCode}
                  </div>
                </React.Fragment>
              );
          })}
          <EarningPeriod formik={formikEarningPeriod} />
        </>
      )}
    </>
  );
};
